import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { findCoursesByName, findNearestCourses, calculateDistance, loadIowaCourses, loadRemainingStates } from '../utils/courseUtils';
import type { CompactCourseModel } from '@/models/CourseModels';
import { COURSE_SEARCH_DEFAULTS } from '../config/courses';
//import LoadingSwinger from './LoadingSwinger';

interface CourseSearchProps {
    onCourseSelect: (course: CompactCourseModel) => void;
}

const CourseSearch: React.FC<CourseSearchProps> = ({ onCourseSelect }) => {
    const { theme } = useTheme();
    const [searchRadius, setSearchRadius] = useState<number>(COURSE_SEARCH_DEFAULTS.initialRadius);
    const [courses, setCourses] = useState<CompactCourseModel[]>([]);
    const [error, setError] = useState<string>('');
    const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
    const [includePrivate, setIncludePrivate] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [searchMode, setSearchMode] = useState<'location' | 'name'>('location');
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const [sortBy, setSortBy] = useState<'distance' | 'name'>('distance');
    const [loadingGPS, setLoadingGPS] = useState(false);
    const [locationName, setLocationName] = useState<string>('');

    // Function to sort courses
    const sortCourses = useCallback((coursesToSort: CompactCourseModel[]) => {
        // Sort by distance, closest to farthest
        if (sortBy == "distance" && userLocation) {
            return [...coursesToSort].sort((a, b) => {
                const distA = calculateDistance(userLocation[0], userLocation[1], a.g[0], a.g[1]);
                const distB = calculateDistance(userLocation[0], userLocation[1], b.g[0], b.g[1]);
                return distA - distB; // Closest first (ascending)
            });
        }

        // Sort alphabetically A-Z
        return [...coursesToSort].sort((a, b) => a.n.localeCompare(b.n));

    }, [sortBy, userLocation]);

    // Function to get user location and find nearest courses
    const getUserLocationAndCourses = async () => {
        if (!('geolocation' in navigator)) {
            setError('Geolocation is not supported by your browser');
            return;
        }

        console.log("getting user location and courses");

        setLoadingGPS(true);
        try {
            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0
                });
            });

            const { latitude, longitude } = position.coords;
            const location: [number, number] = [latitude, longitude];
            setUserLocation(location);

            // Get location name using reverse geocoding
            try {
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
                );
                const data = await response.json();
                console.log("address data:", data);
                if (data.address) {
                    const city = data.address.city || data.address.town || data.address.village || '';
                    const state = data.address.state || '';
                    const county = data.address.county || '';
                    const postcode = data.address.postcode || '';
                    setLocationName(city ? `${city}, ${state} ${postcode}` : county ? `${county}, ${state} ${postcode}` : '(unavailable0)');
                } else {
                    setLocationName('(unavailable1)');
                }
            } catch (err) {
                console.error('Error getting location name:', err);
                setLocationName('(unavailable2)');
            }

            // If we're in location search mode, update the course list
            if (searchMode === 'location') {
                const searchResults = await findNearestCourses(latitude, longitude, searchRadius);
                const filteredResults = includePrivate
                    ? searchResults
                    : searchResults.filter(course => course.t !== 'Private');
                setCourses(filteredResults);
            }
        } catch (err) {
            setLocationName('(unavailable3)');
            console.error('Geolocation error:', err);
            setError('Unable to get your location');
        } finally {
            setLoadingGPS(false);
        }
    };

    // Effect to sort courses whenever they change or sort criteria changes
    useEffect(() => {
        setCourses(prevCourses => sortCourses([...prevCourses]));
    }, [sortCourses, sortBy]);

    // Initial load
    useEffect(() => {
        const initialize = async () => {
            try {
                // Load Iowa courses immediately
                await loadIowaCourses();

                // Start loading other states in the background
                loadRemainingStates().catch(err => {
                    console.error('Error loading remaining states:', err);
                });

                // If in location mode, get user location
                if (searchMode === 'location') {
                    await getUserLocationAndCourses();
                }
            } catch (err) {
                console.error('Error during initialization:', err);
            }
        };
        initialize();
    }, []);

    const handleLocationSearch = useCallback(async () => {
        if (!userLocation) {
            setError('Please enable location services to search for nearby courses');
            return;
        }

        setError('');
        try {
            const [latitude, longitude] = userLocation;
            const nearbyCourses = await findNearestCourses(latitude, longitude, searchRadius);

            // Filter private courses if needed
            const filteredCourses = includePrivate
                ? nearbyCourses
                : nearbyCourses.filter(course => course.t !== 'Private');

            // Apply current sort before setting courses
            setCourses(sortCourses(filteredCourses));
        } catch (err) {
            console.error('Error searching by location:', err);
            setError('Failed to find courses near your location');
        }
    }, [userLocation, searchRadius, includePrivate, sortCourses]);

    // Debounced search function
    const debouncedSearch = useCallback(async (term: string) => {
        if (!term.trim()) {
            setCourses([]);
            return;
        }

        setError('');
        try {
            const matchingCourses = await findCoursesByName(term);

            // Filter private courses if needed
            const filteredCourses = includePrivate
                ? matchingCourses
                : matchingCourses.filter(course => course.t !== 'Private');

            // Apply current sort before setting courses
            setCourses(sortCourses(filteredCourses));
        } catch (err) {
            console.error('Error searching by name:', err);
            setError('Failed to find courses matching the search term');
        }
    }, [includePrivate, sortCourses]);

    // Handle search term changes with debounce
    const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newTerm = e.target.value;
        setSearchTerm(newTerm);

        // Clear any existing timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        // Set new timeout for debounced search
        const timeout = setTimeout(() => {
            debouncedSearch(newTerm);
        }, 750);

        setSearchTimeout(timeout);
    };

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    const handleRadiusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.target.value, 10);
        setSearchRadius(value);
        if (searchMode === 'location' && userLocation) {
            handleLocationSearch();
        }
    };

    const handleIncludePrivateChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setIncludePrivate(event.target.checked);

        // Re-run the appropriate search to get fresh results
        if (searchMode === 'location' && userLocation) {
            const [latitude, longitude] = userLocation;
            const nearbyCourses = await findNearestCourses(latitude, longitude, searchRadius);
            const filteredCourses = event.target.checked
                ? nearbyCourses
                : nearbyCourses.filter(course => course.t !== 'Private');
            // Apply current sort before setting courses
            setCourses(sortCourses(filteredCourses));
        } else if (searchMode === 'name' && searchTerm.trim()) {
            const matchingCourses = await findCoursesByName(searchTerm);
            const filteredCourses = event.target.checked
                ? matchingCourses
                : matchingCourses.filter(course => course.t !== 'Private');
            // Apply current sort before setting courses
            setCourses(sortCourses(filteredCourses));
        }
    };

    const formatDistance = (km: number): string => {
        const miles = km / 1.60934;
        if (miles < 1) {
            const yards = Math.round(miles * 1760);
            return `${yards} yd.`;
        }
        return `${miles.toFixed(1)} mi.`;
    };

    return (
        <div style={{ margin: '0 auto', padding: theme.spacing.MEDIUM }}>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing.SMALL
            }}>
                {/* Search Controls - More Compact */}
                <div style={{
                    backgroundColor: theme.colors.SURFACE,
                    padding: theme.spacing.MEDIUM,
                    borderRadius: '8px',
                    border: `1px solid ${theme.colors.BORDER}`,
                    marginBottom: theme.spacing.SMALL,
                }}>
                    {/* Search Mode Toggle - More Compact */}
                    <div style={{
                        display: 'flex',
                        width: '100%',
                        marginBottom: theme.spacing.SMALL,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: `1px solid ${theme.colors.BORDER}`,
                    }}>
                        <button
                            onClick={() => setSearchMode('location')}
                            style={{
                                flex: 1,
                                padding: theme.spacing.SMALL,
                                border: 'none',
                                backgroundColor: searchMode === 'location' ? theme.colors.PRIMARY : theme.colors.BACKGROUND,
                                color: searchMode === 'location' ? theme.colors.WHITE : theme.colors.TEXT,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: theme.spacing.SMALL,
                                fontSize: '0.9em'
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zM12 11.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                                    fill={searchMode === 'location' ? theme.colors.WHITE : theme.colors.TEXT} />
                            </svg>
                            <span>By Location</span>
                        </button>
                        <button
                            onClick={() => setSearchMode('name')}
                            style={{
                                flex: 1,
                                padding: theme.spacing.SMALL,
                                border: 'none',
                                borderLeft: `1px solid ${theme.colors.BORDER}`,
                                backgroundColor: searchMode === 'name' ? theme.colors.PRIMARY : theme.colors.BACKGROUND,
                                color: searchMode === 'name' ? theme.colors.WHITE : theme.colors.TEXT,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: theme.spacing.SMALL,
                                fontSize: '0.9em'
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
                                    fill={searchMode === 'name' ? theme.colors.WHITE : theme.colors.TEXT} />
                            </svg>
                            <span>By Name</span>
                        </button>
                    </div>

                    {/* Search Input Section - More Compact */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: theme.spacing.SMALL
                    }}>
                        {searchMode === 'name' ? (
                            <div style={{
                                position: 'relative',
                                width: '100%'
                            }}>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={handleSearchTermChange}
                                    placeholder="Search for a course..."
                                    style={{
                                        width: '100%',
                                        padding: theme.spacing.SMALL,
                                        paddingLeft: '32px',
                                        border: `1px solid ${theme.colors.BORDER}`,
                                        borderRadius: '4px',
                                        fontSize: '0.9em',
                                        backgroundColor: theme.colors.BACKGROUND,
                                        color: theme.colors.TEXT
                                    }}
                                />
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{
                                        position: 'absolute',
                                        left: '8px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: theme.colors.TEXT_SECONDARY
                                    }}
                                >
                                    <path
                                        d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </div>
                        ) : (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing.SMALL
                            }}>
                                <button
                                    onClick={getUserLocationAndCourses}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: theme.spacing.SMALL,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: theme.colors.TEXT
                                    }}
                                    title="Refresh location"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 12c0-4.4 3.6-8 8-8 3.3 0 6.1 2 7.3 4.8M22 12c0 4.4-3.6 8-8 8-3.3 0-6.1-2-7.3-4.8" />
                                    </svg>
                                </button>
                                <div style={{
                                    flex: 1,
                                    color: theme.colors.TEXT_SECONDARY,
                                    fontSize: '0.8em'
                                }}>
                                    {loadingGPS ? 'Loading...' : locationName}
                                </div>
                            </div>
                        )}

                        {/* Radius and Private Course Controls - More Compact */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: theme.spacing.SMALL
                        }}>
                            {searchMode === 'location' && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: theme.spacing.SMALL,
                                    flex: 1
                                }}>
                                    <span style={{
                                        color: theme.colors.TEXT,
                                        fontSize: '0.8em',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        Radius: {searchRadius} mi
                                    </span>
                                    <input
                                        type="range"
                                        min="5"
                                        max="100"
                                        value={searchRadius}
                                        onChange={handleRadiusChange}
                                        style={{
                                            flex: 1,
                                            accentColor: theme.colors.PRIMARY
                                        }}
                                    />
                                </div>
                            )}

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing.SMALL
                            }}>
                                <input
                                    type="checkbox"
                                    id="includePrivate"
                                    checked={includePrivate}
                                    onChange={handleIncludePrivateChange}
                                    style={{
                                        accentColor: theme.colors.PRIMARY
                                    }}
                                />
                                <label
                                    htmlFor="includePrivate"
                                    style={{
                                        color: theme.colors.TEXT,
                                        fontSize: '0.8em',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Include private
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Course List - More Prominent */}
                <div style={{
                    backgroundColor: theme.colors.SURFACE,
                    padding: 0,
                    borderRadius: '8px',
                    border: `1px solid ${theme.colors.BORDER}`,
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: theme.colors.SURFACE,
                        padding: theme.spacing.MEDIUM,
                        paddingBottom: theme.spacing.SMALL,
                        borderBottom: `1px solid ${theme.colors.BORDER}`,
                    }}>
                        <h3 style={{
                            color: theme.colors.TEXT,
                            margin: 0,
                            fontSize: '1.1em'
                        }}>
                            {courses.length > 0 ? `Courses (${courses.length})` : 'No courses found'}
                        </h3>
                        {courses.length > 0 && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing.SMALL
                            }}>
                                <div style={{
                                    display: 'flex',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    border: `1px solid ${theme.colors.BORDER}`,
                                }}>
                                    <button
                                        onClick={() => setSortBy('distance')}
                                        style={{
                                            padding: theme.spacing.SMALL,
                                            paddingLeft: theme.spacing.MEDIUM,
                                            paddingRight: theme.spacing.MEDIUM,
                                            border: 'none',
                                            backgroundColor: sortBy === 'distance' ? theme.colors.PRIMARY : theme.colors.BACKGROUND,
                                            color: sortBy === 'distance' ? theme.colors.WHITE : theme.colors.TEXT,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: theme.spacing.SMALL,
                                            fontSize: '0.8em'
                                        }}
                                    >
                                        Distance
                                    </button>
                                    <button
                                        onClick={() => setSortBy('name')}
                                        style={{
                                            padding: theme.spacing.SMALL,
                                            paddingLeft: theme.spacing.MEDIUM,
                                            paddingRight: theme.spacing.MEDIUM,
                                            border: 'none',
                                            borderLeft: `1px solid ${theme.colors.BORDER}`,
                                            backgroundColor: sortBy === 'name' ? theme.colors.PRIMARY : theme.colors.BACKGROUND,
                                            color: sortBy === 'name' ? theme.colors.WHITE : theme.colors.TEXT,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: theme.spacing.SMALL,
                                            fontSize: '0.8em'
                                        }}
                                    >
                                        Name
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div style={{
                        padding: theme.spacing.MEDIUM,
                        paddingTop: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: theme.spacing.SMALL
                    }}>
                        {error && (
                            <div style={{
                                padding: theme.spacing.SMALL,
                                backgroundColor: theme.colors.ERROR,
                                color: theme.colors.WHITE,
                                borderRadius: '4px',
                                marginBottom: theme.spacing.SMALL,
                                fontSize: '0.9em'
                            }}>
                                {error}
                            </div>
                        )}

                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: theme.spacing.SMALL
                        }}>
                            {courses.map((course) => (
                                <button
                                    key={course.i}
                                    onClick={() => onCourseSelect(course)}
                                    style={{
                                        width: '100%',
                                        padding: theme.spacing.MEDIUM,
                                        backgroundColor: theme.colors.BACKGROUND,
                                        color: theme.colors.TEXT,
                                        border: `1px solid ${theme.colors.BORDER}`,
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: theme.spacing.SMALL,
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = theme.colors.BORDER;
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = theme.colors.BACKGROUND;
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    {/* Course Name and Location */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.SMALL }}>
                                        <div style={{
                                            fontSize: '1.2em',
                                            fontWeight: 'bold',
                                            color: theme.colors.PRIMARY
                                        }}>
                                            {course.n}
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            fontSize: '0.9em',
                                            color: theme.colors.TEXT_SECONDARY
                                        }}>
                                            <div>{course.c}, {course.s}</div>
                                            {userLocation && (
                                                <div>
                                                    {formatDistance(calculateDistance(
                                                        userLocation[0],
                                                        userLocation[1],
                                                        course.g[0],
                                                        course.g[1]
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Course Details */}
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: theme.spacing.SMALL,
                                        width: '100%',
                                        color: theme.colors.TEXT,
                                        fontSize: '0.9em'
                                    }}>
                                        {/* First row: holes, yards, par */}
                                        <div style={{
                                            display: 'flex',
                                            gap: theme.spacing.MEDIUM,
                                            justifyContent: 'space-between'
                                        }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <span style={{ fontWeight: 'bold' }}>{course.h}</span>
                                                <span style={{ color: theme.colors.TEXT_SECONDARY }}>holes</span>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <span style={{ fontWeight: 'bold' }}>{course.y[1]}</span>
                                                <span style={{ color: theme.colors.TEXT_SECONDARY }}>yards</span>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <span style={{ fontWeight: 'bold' }}>{course.p}</span>
                                                <span style={{ color: theme.colors.TEXT_SECONDARY }}>par</span>
                                            </div>
                                        </div>
                                        {/* Second row: rating and slope */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            borderTop: `1px solid ${theme.colors.BORDER}`,
                                            paddingTop: theme.spacing.SMALL
                                        }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <span style={{ fontWeight: 'bold' }}>{course.r}</span>
                                                <span style={{ color: theme.colors.TEXT_SECONDARY }}>rating</span>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <span style={{ fontWeight: 'bold' }}>{course.l}</span>
                                                <span style={{ color: theme.colors.TEXT_SECONDARY }}>slope</span>
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CourseSearch; 